import React, { FC, useCallback, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AsyncTypeahead, Token } from 'react-bootstrap-typeahead';
import { Col, Form, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';
import { Account, FailCodeApproverSetting, Retailer } from '@packages/models/api';
import { Button, Typography } from '@packages/ui/shared';
import { getLogger } from '@packages/core/logging';
import { accountsService, qmrsService, techlineService } from '@web/services/singletons';
import { useAuthState } from '@packages/contexts/auth';
import AsyncPage from '@web/components/async-page';
import { DetailView, DetailViewBody, DetailViewContent, DetailHeader } from '@web/components/detail-view';
import Select from '@web/components/select';
import { formatToTitleCase } from '@web/utils';
import { GetAccountsByRetailerResponseBody } from '@packages/contexts/accounts';

const logger = getLogger('admin');

const useStyles = createUseStyles({
    asyncTypeahead: {
        '& .rbt-input-wrapper': {
            alignItems: 'center !important',
            '& .rbt-token': {
                alignItems: 'center',
                borderRadius: '4px !important',
                display: 'flex',
                fontSize: '11px',
                fontWeight: 700,
                height: '20px',
                padding: '12px',
            },
            '& button.close': {
                height: 'auto',
                margin: '0',
            },
        },
        '& .rbt-aux': {
            display: 'none',
        },
    },
});

export const RetailerForm: FC = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const classes = useStyles();
    const { account } = useAuthState();
    const { retailerId } = useParams<{ retailerId?: string }>();
    const [retailer, setRetailer] = useState<Retailer>();
    const [approverSettings, setApproverSettings] = useState<FailCodeApproverSetting[]>([]);
    const [failCodeApprovers, setFailCodeApprovers] = useState<Account[]>([]);
    const [selectedApproverSetting, setSelectedApproverSetting] = useState('');
    const [caseMembers, setCaseMembers] = useState<GetAccountsByRetailerResponseBody[]>([]);
    const canAddCaseMemberForRetailer = account?.systemCapabilities.canAddCaseMemberForRetailer;
    const canViewFailCodeApprover = account?.systemCapabilities.canViewFailCodeApprover;

    const fetchData = useCallback(async () => {
        if (!retailerId) {
            return;
        }

        if (canViewFailCodeApprover) {
            qmrsService
                .fetchFailCodeApproverSettings({})
                .then((response) => {
                    if (!response.success && response.aborted) {
                        return;
                    } else if (!response.success) {
                        throw response.data;
                    }

                    setApproverSettings(response.data.failCodeApproverSettings);
                })
                .catch((e) => {
                    logger.error('Failed to fetch fail code approver settings', { error: e });
                });
        }

        qmrsService.fetchRetailerbyId({ retailerId }).then((response) => {
            if (!response.success && response.aborted) {
                return;
            } else if (!response.success) {
                throw response.data;
            }

            setRetailer(response.data.retailer);
            setSelectedApproverSetting(response.data.retailer.failCodeApproverSettingId);
        });

        if (canViewFailCodeApprover) {
            qmrsService
                .fetchRetailerFailCodeApprovers({ retailerId })
                .then((response) => {
                    if (!response.success && response.aborted) {
                        return;
                    } else if (!response.success) {
                        throw response.data;
                    }
                    setFailCodeApprovers(response.data.accounts);
                })
                .catch((e) => {
                    logger.error(`Failed to fetch fail code approvers for retailer [${retailerId}]`, {
                        error: e,
                        retailerId,
                    });
                });
        }

        if (canAddCaseMemberForRetailer) {
            techlineService.fetchRetailerCaseMembers({ retailerId }).then((response) => {
                if (!response.success && response.aborted) {
                    return;
                } else if (!response.success) {
                    throw response.data;
                }
                // Had to cast here because the endpoint returns a partial Account type
                // and the typeahead expects a full GetAccountsByRetailerResponseBody[]
                setCaseMembers(response.data.accounts as unknown as GetAccountsByRetailerResponseBody[]);
            });
        }
    }, [retailerId]);

    function handleCancelButtonPress() {
        navigate(-1);
    }

    async function handleSaveButtonPress() {
        if (!retailerId) {
            return;
        }
        try {
            if (canViewFailCodeApprover) {
                await qmrsService
                    .updateRetailerFailCodeApprovers({
                        retailerId,
                        accountIds: failCodeApprovers.map((fCA) => fCA.accountId),
                    })
                    .then((response) => {
                        if (!response.success && response.aborted) {
                            return;
                        } else if (!response.success) {
                            throw response.data;
                        }
                    });

                await qmrsService
                    .updateRetailerApproverSetting({
                        retailerId,
                        approverSettingId: selectedApproverSetting,
                    })
                    .then((response) => {
                        if (!response.success && response.aborted) {
                            return;
                        } else if (!response.success) {
                            throw response.data;
                        }
                    })
                    .catch((e) => {
                        logger.error(`Failed to update the approver setting for retailer [${retailerId}]`, {
                            error: e,
                            retailerId,
                            approverSettingId: selectedApproverSetting,
                        });
                    });
            }

            if (canAddCaseMemberForRetailer) {
                await techlineService
                    .updateRetailerCaseMembers({
                        retailerId,
                        accountIds: caseMembers.map((caseMember) => caseMember.accountId),
                    })
                    .then((response) => {
                        if (!response.success && response.aborted) {
                            return;
                        } else if (!response.success) {
                            throw response.data;
                        }
                    })
                    .catch((e) => {
                        logger.error(`Failed to update case members for retailer [${retailerId}]`, {
                            error: e,
                            retailerId,
                        });
                    });
            }

            navigate(-1);
        } catch (e) {
            window.alert(e.message);
        }
    }

    /* ------------------------------------------------- */
    /* Approvers Typeahead */
    /* ------------------------------------------------- */
    const [approversTypeaheadIsLoading, setManagersTypeaheadIsLoading] = useState(false);
    const [approversTypeheadOptions, setManagersTypeaheadOptions] = useState<Account[]>([]);

    const [caseMembersTypeaheadIsLoading, setCaseMembersTypeaheadIsLoading] = useState(false);
    const [caseMembersTypeheadOptions, setCaseMembersTypeaheadOptions] = useState<GetAccountsByRetailerResponseBody[]>(
        []
    );

    const handleManagersTypeaheadSearch = useCallback(
        async (query: string) => {
            setManagersTypeaheadIsLoading(true);

            try {
                const response = await accountsService.getAccounts({
                    query,
                });
                if (!response.success && response.aborted) {
                    return;
                } else if (!response.success) {
                    throw response.data;
                }

                const managersToShow = response.data.accounts.filter((account) => {
                    const matchFound = failCodeApprovers.some((manager) => manager.accountId === account.accountId);

                    return account.enabled && !matchFound;
                });

                setManagersTypeaheadOptions(managersToShow);
            } catch (error) {
                setManagersTypeaheadOptions([]);
                window.alert(error.message);
            }

            setManagersTypeaheadIsLoading(false);
        },
        [failCodeApprovers]
    );

    const handleCaseMembersTypeaheadSearch = useCallback(
        async (query: string) => {
            setCaseMembersTypeaheadIsLoading(true);

            try {
                if (!retailer) {
                    return;
                }
                const response = await accountsService.getAccountsByRetailerNumber({
                    text: query,
                    retailerCode: retailer?.code,
                });
                if (!response.success && response.aborted) {
                    return;
                } else if (!response.success) {
                    throw response.data;
                }

                const accountsToShow = response.data.filter((account) => {
                    const matchFound = caseMembers.some((caseMember) => caseMember.accountId === account.accountId);

                    return account.enabled && !matchFound;
                });

                setCaseMembersTypeaheadOptions(accountsToShow);
            } catch (error) {
                setCaseMembersTypeaheadOptions([]);
                window.alert(error.message);
            }

            setCaseMembersTypeaheadIsLoading(false);
        },
        [caseMembers, retailer]
    );

    const handleApproversTypeaheadChange = useCallback((selected: Account[]) => {
        if (!selected) {
            return;
        }

        setFailCodeApprovers(selected);
    }, []);

    const handleCaseMembersTypeaheadChange = useCallback((selected: GetAccountsByRetailerResponseBody[]) => {
        if (!selected) {
            return;
        }

        setCaseMembers(selected);
    }, []);

    const commonBreadcrumbs = [
        {
            to: '/administration',
            title: t('administration:breadcrumbs.adminHub', 'Administration Hub'),
        },
        {
            to: '/administration/retailers',
            title: t('retailers:breadcrumbs.manageRetailers', 'Manage Retailers'),
        },
    ];

    const breadcrumbs = retailerId
        ? [
              ...commonBreadcrumbs,
              {
                  to: `/administration/retailers/${retailerId}`,
                  title: t('retailers:breadcrumbs.retailer', 'Retailer'),
              },
              {
                  to: `/administration/retailers/${retailerId}/edit`,
                  title: t('retailers:breadcrumbs.editRetailer', 'Edit'),
              },
          ]
        : undefined;

    const rightSideItems = [
        <Button variant="ghost-blue" key="cancel" onPress={handleCancelButtonPress}>
            Cancel
        </Button>,
        <Button key="save" onPress={handleSaveButtonPress}>
            Save Retailer
        </Button>,
    ];

    return (
        <AsyncPage fetchData={fetchData}>
            <DetailView>
                <DetailHeader
                    rightSideItems={rightSideItems}
                    breadcrumbs={breadcrumbs}
                    hasBack={true}
                    subtitle={retailer && `${retailer?.name} (#${retailer?.code})`}
                ></DetailHeader>
                <DetailViewContent topOffset={retailerId ? 'breadcrumb' : 'header'}>
                    <DetailViewBody>
                        {canViewFailCodeApprover && (
                            <>
                                <Row className="mb-8">
                                    <Col>
                                        <Typography variant="h3">
                                            {t('retailers:manageRetailers.failCodeSettings.title')}
                                        </Typography>
                                    </Col>
                                </Row>

                                <Row>
                                    <Col xs={6}>
                                        <Form.Group>
                                            <Col className="mb-2">
                                                <Typography>
                                                    {t('retailers:manageRetailers.failCodeSettings.approvalSetting')}
                                                </Typography>
                                            </Col>
                                            <Select
                                                value={selectedApproverSetting}
                                                options={approverSettings.map((aS) => {
                                                    return {
                                                        value: aS.failCodeApproverSettingId,
                                                        title: aS.description,
                                                    };
                                                })}
                                                onChange={(event: React.ChangeEvent<HTMLSelectElement>) => {
                                                    setSelectedApproverSetting(event.target.value);
                                                }}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <Row className="mt-4">
                                    <Col xs={6}>
                                        <Form.Group>
                                            <Col className="mb-2">
                                                <Typography>
                                                    {t(
                                                        'retailers:manageRetailers.failCodeSettings.authorizedApprovers'
                                                    )}
                                                </Typography>
                                            </Col>
                                            <AsyncTypeahead
                                                className={classes.asyncTypeahead}
                                                id="approvers-async-typeahead"
                                                isLoading={approversTypeaheadIsLoading}
                                                labelKey={(acct) =>
                                                    `${formatToTitleCase(acct.name)} ${
                                                        acct.soaUsername ? `(${acct.soaUsername})` : ''
                                                    }`
                                                }
                                                minLength={1}
                                                multiple
                                                onChange={handleApproversTypeaheadChange}
                                                onSearch={handleManagersTypeaheadSearch}
                                                options={approversTypeheadOptions}
                                                placeholder={t(
                                                    'retailers:manageRetailers.failCodeSettings.authorizedApproversPlaceholder'
                                                )}
                                                selected={failCodeApprovers}
                                                useCache={false}
                                                renderToken={(option, { onRemove, tabIndex }, index) => (
                                                    <Token
                                                        key={index}
                                                        option={option}
                                                        onRemove={onRemove}
                                                        tabIndex={tabIndex}
                                                    >
                                                        {formatToTitleCase(option.name)}
                                                    </Token>
                                                )}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </>
                        )}
                        {canAddCaseMemberForRetailer && (
                            <>
                                <Row className="mb-8 mt-8">
                                    <Col>
                                        <Typography variant="h3">
                                            {t('retailers:manageRetailers.techlineSettings.title')}
                                        </Typography>
                                    </Col>
                                </Row>
                                <Row className="mt-4">
                                    <Col xs={6}>
                                        <Form.Group>
                                            <Col className="mb-2">
                                                <Typography>
                                                    {t('retailers:manageRetailers.techlineSettings.description')}
                                                </Typography>
                                            </Col>
                                            <AsyncTypeahead
                                                className={classes.asyncTypeahead}
                                                id="case-members-async-typeahead"
                                                isLoading={caseMembersTypeaheadIsLoading}
                                                labelKey={(acct) => {
                                                    const name = formatToTitleCase(
                                                        `${acct.firstName} ${acct.lastName}`
                                                    );
                                                    return `${name} ${acct.soaUsername ? `(${acct.soaUsername})` : ''}`;
                                                }}
                                                minLength={1}
                                                multiple
                                                onChange={handleCaseMembersTypeaheadChange}
                                                onSearch={handleCaseMembersTypeaheadSearch}
                                                options={caseMembersTypeheadOptions}
                                                placeholder={t(
                                                    'retailers:manageRetailers.techlineSettings.placeholder'
                                                )}
                                                selected={caseMembers}
                                                useCache={false}
                                                renderToken={(option, { onRemove, tabIndex }, index) => (
                                                    <Token
                                                        key={index}
                                                        option={option}
                                                        onRemove={onRemove}
                                                        tabIndex={tabIndex}
                                                    >
                                                        {formatToTitleCase(`${option.firstName} ${option.lastName}`)}
                                                    </Token>
                                                )}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </>
                        )}
                    </DetailViewBody>
                </DetailViewContent>
            </DetailView>
        </AsyncPage>
    );
};
